import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, BotIcon, ArrowRightIcon, ZapIcon, PhoneIcon, PlayIcon } from 'lucide-react';
import Button from './Button';
import Wizard from './Wizard';
import { useTranslation } from '../i18n/useTranslation';
const DemoInterface = () => {
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const navigate = useNavigate();
  const {
    t
  } = useTranslation();
  const handleLearnMore = () => {
    navigate('/gpt-store');
    window.scrollTo(0, 0);
  };
  return <section id="demo-interface" className="py-16 bg-gradient-to-b from-gray-50 to-white" data-id="element-1366">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1367">
        <div className="text-center mb-12" data-id="element-1368">
          <h2 className="text-3xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-1369">
            See Shopilots in Action
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1370">
            Create your own AI shopping advisor in minutes and deploy it across
            multiple platforms
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 max-w-6xl mx-auto border border-indigo-100 transform transition-all hover:shadow-xl duration-300" data-id="element-1371">
          <div className="flex flex-col lg:flex-row items-center gap-8" data-id="element-1372">
            <div className="lg:w-1/2" data-id="element-1373">
              <div className="bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full w-16 h-16 flex items-center justify-center mb-4" data-id="element-1374">
                <BotIcon className="h-8 w-8 text-white" data-id="element-1375" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-indigo-800" data-id="element-1376">
                Create Your AI Agent
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-1377">
                Connect your product catalog, customize your AI agent's goals,
                and deploy it across all your customer touchpoints.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-1378">
                <li className="flex items-start" data-id="element-1379">
                  <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1380" />
                  <span className="ml-3 text-gray-600" data-id="element-1381">
                    Easy setup in minutes
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1382">
                  <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1383" />
                  <span className="ml-3 text-gray-600" data-id="element-1384">No coding required</span>
                </li>
                <li className="flex items-start" data-id="element-1385">
                  <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1386" />
                  <span className="ml-3 text-gray-600" data-id="element-1387">
                    Works across all platforms
                  </span>
                </li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1388">
                <a href="tel:+19146011670" className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-purple-600 text-white hover:bg-purple-700 shadow-md hover:shadow-lg transition-all duration-300 font-medium" data-id="element-1389">
                  <PhoneIcon className="mr-2 h-5 w-5" data-id="element-1390" />
                  Call Us
                </a>
                <button onClick={handleLearnMore} className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-white text-purple-600 border-2 border-purple-600 hover:bg-gray-50 shadow-md hover:shadow-lg transition-all duration-300 font-medium" data-id="element-1391">
                  Learn More
                  <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1392" />
                </button>
              </div>
            </div>

            <div className="lg:w-1/2 w-full" data-id="element-1393">
              {/* Mobile: Full width video */}
              <div className="lg:hidden w-full" data-id="element-1394">
                <div className="relative rounded-xl overflow-hidden shadow-lg bg-black" data-id="element-1395">
                  <div className="w-full" style={{
                  paddingBottom: '177.78%'
                }} data-id="element-1396">
                    <iframe className="absolute inset-0 w-full h-full" src="https://app.heygen.com/embedded-player/c820e5db87424fa4a7df4dd131e843c0" title="HeyGen video player" frameBorder="0" allow="encrypted-media; fullscreen;" allowFullScreen data-id="element-1397"></iframe>
                  </div>
                </div>
              </div>

              {/* Desktop: Phone mockup frame */}
              <div className="hidden lg:flex justify-center items-center" data-id="element-1398">
                <div className="relative" data-id="element-1399">
                  {/* Phone frame mockup */}
                  <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-[3rem] p-3 shadow-2xl" data-id="element-1400">
                    {/* Notch */}
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-40 h-7 bg-black rounded-b-3xl z-10" data-id="element-1401"></div>

                    {/* Screen */}
                    <div className="relative bg-black rounded-[2.5rem] overflow-hidden" style={{
                    width: '375px',
                    height: '667px'
                  }} data-id="element-1402">
                      <iframe className="w-full h-full" src="https://app.heygen.com/embedded-player/c820e5db87424fa4a7df4dd131e843c0" title="HeyGen video player" frameBorder="0" allow="encrypted-media; fullscreen;" allowFullScreen data-id="element-1403"></iframe>
                    </div>

                    {/* Home indicator */}
                    <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gray-600 rounded-full" data-id="element-1404"></div>
                  </div>

                  {/* Decorative glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-[3rem] blur-2xl -z-10" data-id="element-1405"></div>
                </div>
              </div>

              <div className="mt-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 shadow-inner" data-id="element-1406">
                <div className="flex items-center gap-3 mb-2" data-id="element-1407">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center" data-id="element-1408">
                    <BotIcon className="h-4 w-4 text-white" data-id="element-1409" />
                  </div>
                  <div data-id="element-1410">
                    <p className="font-medium text-indigo-800" data-id="element-1411">
                      Shopilots AI Agent
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1412">Watch how it works</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600" data-id="element-1413">
                  "See how our AI shopping assistant helps customers find the
                  perfect products, answers questions, and drives sales across
                  all platforms."
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Wizard isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} data-id="element-1414" />
    </section>;
};
export default DemoInterface;